import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach auth token from localStorage on every request
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors globally
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('streamToken');
      localStorage.removeItem('streamUser');
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class AuthApiService {
  // Login
  static async login(username, password) {
    try {
      const response = await authApi.post('/auth/login', { username, password });
      return response.data;
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  }

  // Register
  static async register(username, email, password, role = 'user') {
    try {
      const response = await authApi.post('/auth/register', { 
        username, 
        email, 
        password, 
        role 
      });
      return response.data;
    } catch (error) {
      console.error('Error registering:', error);
      throw error;
    }
  }

  // Get current user
  static async getCurrentUser() {
    try {
      const response = await authApi.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  // Change password
  static async changePassword(currentPassword, newPassword) {
    try {
      const response = await authApi.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  // Get all users (admin only)
  static async getAllUsers() {
    try {
      const response = await authApi.get('/auth/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Alias for getAllUsers (used by RoleManagement)
  static async getUsers() {
    return this.getAllUsers();
  }

  // Update user (admin only)
  static async updateUser(id, updates) {
    try {
      const response = await authApi.put(`/auth/users/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete user (admin only)
  static async deleteUser(id) {
    try {
      const response = await authApi.delete(`/auth/users/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Setup default admin
  static async setupAdmin() {
    try {
      const response = await authApi.post('/auth/setup');
      return response.data;
    } catch (error) {
      console.error('Error setting up admin:', error);
      throw error;
    }
  }
}

export default AuthApiService;
